import apiV2 from "api-v2";
import type {
	KeyPersonnelDetail,
	KeyPersonnelFormValues,
	KeyPersonnelListParams,
	KeyPersonnelPageResponse,
	PastProjectSearchResult,
} from "./types/KeyPersonnel.type";

const BASE_URL = "key-personnel";

export const getKeyPersonnelListPaginated = async (
	params: KeyPersonnelListParams = {},
	page = 1
): Promise<KeyPersonnelPageResponse> => {
	const { data } = await apiV2.get(BASE_URL, {
		params: { ...params, page, per_page: params.per_page ?? 10 },
	});
	return data?.data ?? data;
};

export const getKeyPersonnel = async (
	id: number
): Promise<KeyPersonnelDetail> => {
	const { data } = await apiV2.get(`${BASE_URL}/${id}`);
	return data?.data ?? data;
};

export const buildFormData = (values: KeyPersonnelFormValues): FormData => {
	const fd = new FormData();
	fd.append("first_name", values.first_name);
	fd.append("last_name", values.last_name);
	fd.append("email", values.email);
	fd.append("position", values.position);
	fd.append("experience", String(values.experience));
	fd.append("is_resume_private", values.is_resume_private ? "1" : "0");

	if (values.sort_order !== null) {
		fd.append("sort_order", String(values.sort_order));
	}

	values.sector_ids.forEach((id) => {
		fd.append("sector_ids[]", String(id));
	});

	if (values.disciplines.length > 0) {
		values.disciplines.forEach((group, i) => {
			fd.append(`disciplines[${i}][expertise]`, group.expertise || "");
			if (group.disciplines?.length) {
				group.disciplines.forEach((dId) => {
					fd.append(`disciplines[${i}][disciplines][]`, String(dId));
				});
			}
		});
	}

	if (values.avatar_image instanceof File) {
		fd.append("avatar_image", values.avatar_image);
	} else if (values.avatar_image === null) {
		// Signal avatar removal
		fd.append("avatar_remove", "1");
	}

	if (values.projects_completed.length > 0) {
		values.projects_completed.forEach((project, i) => {
			fd.append(`projects_completed[${i}][name]`, project.name || "");
			fd.append(`projects_completed[${i}][role]`, project.role || "");
			// Send sector_ids array for multi-sector support
			if (project.sector_ids?.length) {
				project.sector_ids.forEach((sid) => {
					fd.append(`projects_completed[${i}][sector_ids][]`, String(sid));
				});
			} else if (
				project.sector_id !== null &&
				project.sector_id !== undefined
			) {
				// Backwards compatibility: single sector_id
				fd.append(
					`projects_completed[${i}][sector_ids][]`,
					String(project.sector_id)
				);
			}
			if (project.location) {
				fd.append(`projects_completed[${i}][location]`, project.location);
			}
			fd.append(`projects_completed[${i}][value]`, project.value || "");
			if (project.project_id !== null && project.project_id !== undefined) {
				fd.append(
					`projects_completed[${i}][project_id]`,
					String(project.project_id)
				);
			}
			fd.append(
				`projects_completed[${i}][isCompanyProject]`,
				project.isCompanyProject ? "1" : "0"
			);
		});
	} else {
		// Send empty array so backend clears existing projects
		fd.append("projects_completed", "");
	}

	values.resume_files.forEach((rf, i) => {
		if (rf.id) {
			fd.append(`resume_files[${i}][id]`, String(rf.id));
		}
		if (rf.file) {
			fd.append(`resume_files[${i}][file]`, rf.file);
		}
		fd.append(`resume_files[${i}][is_default]`, rf.is_default ? "1" : "0");
		if (rf.is_remove) {
			fd.append(`resume_files[${i}][is_remove]`, "1");
		}
	});

	return fd;
};

export const createKeyPersonnel = async (
	formData: FormData
): Promise<KeyPersonnelDetail> => {
	const { data } = await apiV2.post(BASE_URL, formData);
	return data?.data ?? data;
};

export const updateKeyPersonnel = async (
	id: number,
	values: FormData
): Promise<KeyPersonnelDetail> => {
	const { data } = await apiV2.post(`${BASE_URL}/${id}`, values, {
		params: { _method: "PATCH" },
	});
	return data?.data ?? data;
};

export const deleteKeyPersonnel = async (id: number): Promise<void> => {
	await apiV2.delete(`${BASE_URL}/${id}`);
};

export const reorderKeyPersonnel = async (
	id: number,
	sortOrder: number
): Promise<void> => {
	await apiV2.post(`${BASE_URL}/${id}/reorder`, { sort_order: sortOrder });
};

export const searchPastProjects = async (params: {
	page?: number;
	page_size?: number;
	search?: string;
}): Promise<{
	pagination: {
		current_page: number;
		last_page: number;
		per_page: number;
		total: number;
	};
	past_projects: PastProjectSearchResult[];
}> => {
	const { data } = await apiV2.get(`${BASE_URL}/past-projects/search`, {
		params,
	});
	return data?.data ?? data;
};
